/**
 * Foto KTP Column Definitions
 *
 * Column definitions for the FotoKtp DataTable view.
 */

import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import type { FotoKtpMember } from "../components/FotoKtpGrid";
import { KtpPhotoCell } from "../components/KtpPhotoCell";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";

function formatDate(dateString: string | null): string {
  if (!dateString) return "-";
  try {
    return format(new Date(dateString), "dd MMM yyyy", { locale: idLocale });
  } catch {
    return "-";
  }
}

function formatPhoneForWhatsApp(phone: string): string {
  let cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("0")) cleaned = "62" + cleaned.substring(1);
  else if (!cleaned.startsWith("62")) cleaned = "62" + cleaned;
  return cleaned;
}

interface FotoKtpColumnCallbacks {
  onZoom: (src: string, title: string) => void;
  onAction: (member: FotoKtpMember, mode: "reject" | "approve") => void;
  tabMode: "semua" | "hold";
}

export function createFotoKtpColumns({
  onZoom,
  onAction,
  tabMode,
}: FotoKtpColumnCallbacks): ColumnDef<FotoKtpMember>[] {
  const columns: ColumnDef<FotoKtpMember>[] = [
    {
      id: "nama",
      accessorFn: (row) => row.nama_lengkap,
      header: "Nama",
      cell: ({ row }) => (
        <div>
          <p className="font-semibold text-sm">{row.original.nama_lengkap}</p>
          <p className="text-xs font-mono text-muted-foreground">{row.original.callsign}</p>
          <p className="text-xs text-muted-foreground">NRI: {row.original.nri || "-"}</p>
        </div>
      ),
      enableSorting: false,
      size: 180,
    },
    {
      id: "kontak",
      accessorFn: (row) => row.no_hp ?? "",
      header: "Kontak",
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5 text-xs">
          {row.original.no_hp && row.original.no_hp !== "-" ? (
            <a
              href={`https://wa.me/${formatPhoneForWhatsApp(row.original.no_hp)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-700 hover:underline whitespace-nowrap flex items-center gap-1"
            >
              <MessageCircle className="h-3 w-3" />
              {row.original.no_hp}
            </a>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
          {row.original.email && (
            <span className="text-muted-foreground break-all text-[11px]">{row.original.email}</span>
          )}
        </div>
      ),
      enableSorting: false,
      size: 150,
    },
    {
      id: "foto",
      header: "Foto Profil",
      cell: ({ row }) =>
        row.original.foto ? (
          <img
            src={row.original.foto}
            alt={row.original.nama_lengkap}
            className="h-16 w-12 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => onZoom(row.original.foto!, `Foto Profil - ${row.original.nama_lengkap}`)}
          />
        ) : (
          <span className="text-muted-foreground text-xs">-</span>
        ),
      enableSorting: false,
      size: 80,
    },
    {
      id: "ktp",
      header: "Foto KTP",
      cell: ({ row }) => (
        <KtpPhotoCell
          userId={row.original._id}
          idcardEncrypted={row.original.idcardEncrypted}
          idcardUrl={row.original.idcardUrl}
          userName={row.original.nama_lengkap}
        />
      ),
      enableSorting: false,
      size: 80,
    },
    {
      id: "uploaded",
      accessorFn: (row) => row.idcardUploadedAt ?? "",
      header: "Diupload",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {formatDate(row.original.idcardUploadedAt)}
        </span>
      ),
      enableSorting: false,
      size: 120,
    },
    {
      id: "status",
      accessorFn: (row) => (row.isFotoValid === false ? "rejected" : "ok"),
      header: "Status",
      cell: ({ row }) =>
        row.original.isFotoValid === false ? (
          <Badge variant="destructive" className="text-[10px]">Ditolak</Badge>
        ) : null,
      enableSorting: false,
      size: 80,
    },
  ];

  // Add rejection reason column for hold tab
  if (tabMode === "hold") {
    columns.push({
      id: "reason",
      accessorFn: (row) => row.fotoRejectionReason ?? "",
      header: "Alasan",
      cell: ({ row }) => (
        <p className="text-xs text-muted-foreground max-w-[200px] break-words">
          {row.original.fotoRejectionReason || "-"}
        </p>
      ),
      enableSorting: false,
      size: 200,
    });
  }

  // Action column
  columns.push({
    id: "actions",
    header: "Aksi",
    cell: ({ row }) =>
      tabMode === "hold" ? (
        <Button size="sm" className="h-7 text-xs" onClick={() => onAction(row.original, "approve")}>
          Lepas
        </Button>
      ) : (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(row.original, "reject")}>
          Tolak
        </Button>
      ),
    enableSorting: false,
    size: 80,
  });

  return columns;
}
